'use client'

import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  InMemoryCache,
  createHttpLink,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { Auth } from 'aws-amplify'
import { useSession } from 'next-auth/react'
import { FC, ReactNode, useMemo } from 'react'
import {
  Comment,
  CommentEdge,
  Reply,
  ReplyEdge,
  Video,
  VideoRecommendsEdge,
} from '~/__generated__/graphql'
import { getLanguageLocalStorage } from '~/features/i18n/utils/languageLocalStorage'
import { getAnonymousUserId } from '../anonymousUserId'
import browserEnv from '../env/browserEnv'
import { cursorConnection } from './cursorConnection'

const httpLink = createHttpLink({
  uri: `${browserEnv.NEXT_PUBLIC_API_HOST}/graphql`,
})

const ApolloProviderWrapper: FC<{ children?: ReactNode }> = ({ children }) => {
  const localStorageLanguage = getLanguageLocalStorage()
  const anonymousUserId = getAnonymousUserId()
  const { data: snsSession } = useSession()

  const client = useMemo(() => {
    const authLink = setContext(async (_, { headers, ...rest }) => {
      let token: string | undefined
      if (snsSession) {
        token = snsSession.access_token
      } else {
        try {
          const session = await Auth.currentSession()
          token = session.getAccessToken().getJwtToken()
        } catch (e) {}
      }

      return {
        ...rest,
        headers: {
          ...headers,
          ...(token !== undefined && { Authorization: `Bearer ${token}` }),
          'X-App-Language': localStorageLanguage ?? 'en',
          'X-Anonymous-User-Id': anonymousUserId,
        },
      }
    })
    return new ApolloClient({
      link: ApolloLink.from([authLink, httpLink]),
      connectToDevTools: process.env.NODE_ENV !== 'production',
      cache: new InMemoryCache({
        typePolicies: {
          VideoCommentsConnection: cursorConnection<CommentEdge, Comment>(),
          CommentRepliesConnection: cursorConnection<ReplyEdge, Reply>(),
          VideoRecommendsConnection: cursorConnection<VideoRecommendsEdge, Video>(),
        },
      }),
    })
  }, [snsSession])

  return <ApolloProvider client={client}>{children}</ApolloProvider>
}

export default ApolloProviderWrapper
